import apiService from './api';
import type { ApiResponse } from '../types';

/**
 * Platform operator views — cross-tenant, super_admin only.
 *
 * Every other service in here is org-scoped by construction. This one is not,
 * and the backend enforces that with its own gate (requireSuperAdmin on the
 * whole /platform router) rather than trusting the caller to have checked.
 */

export interface PlatformTotals {
  active_orgs: number;
  inactive_orgs: number;
  active_users: number;
  inactive_users: number;
  unverified_users: number;
  /** Accounts belonging to no organisation at all — the triage queue. */
  orphan_users: number;
  active_subscriptions: number;
}

export interface PlatformSignup {
  id: number;
  username: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  email_verified: boolean;
  is_active?: boolean;
  created_at: string;
  organisation_id?: number | null;
  organisation_name?: string | null;
}

export interface PlatformRecentOrg {
  id: number;
  name: string;
  contact_email: string | null;
  created_at: string;
  member_count: number;
}

export interface PlatformOverview {
  window_days: number;
  totals: PlatformTotals;
  signup_trend: { day: string; signups: number }[];
  recent_signups: PlatformSignup[];
  orphan_users: PlatformSignup[];
  recent_orgs: PlatformRecentOrg[];
}

export interface PlatformOrganisation {
  id: number;
  name: string;
  is_active: boolean;
  created_at: string;
  contact_email: string | null;
  timezone: string | null;
  subscription_plan: string | null;
  member_count: number;
  active_user_count: number;
  active_subscriptions: number;
  total_seats: number;
  assigned_seats: number;
  /** Most recent refresh-token use by anyone in the org. Null = never signed in since tokens were tracked. */
  last_active_at: string | null;
}

export interface PlatformUser extends PlatformSignup {
  is_active: boolean;
  org_count: number;
  last_active_at: string | null;
}

export type PlatformUserFilter = 'orphans' | 'unverified' | 'inactive';

/**
 * One distinct frontend error, aggregated server-side by fingerprint.
 *
 * `occurrences` is the count across every report sharing that fingerprint —
 * the list is a worklist of distinct bugs, not a feed of events.
 */
export interface ClientErrorReport {
  id: number;
  fingerprint: string;
  name: string;
  message: string;
  /** Minified — there is no symbolication step. See errorReporter.ts. */
  stack: string | null;
  release: string | null;
  platform: string | null;
  path: string | null;
  source: 'render' | 'window' | 'unhandledrejection' | 'manual';
  occurrences: number;
  affected_users: number;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
  user_agent: string | null;
  last_org_id: number | null;
  last_org_name: string | null;
}

export interface ClientErrorSummary {
  unresolved: number;
  last_24h: number;
  occurrences_24h: number;
}

class PlatformService {
  async getOverview(days = 30): Promise<ApiResponse<PlatformOverview>> {
    return apiService.get(`/platform/overview?days=${days}`);
  }

  async getOrganisations(
    opts: { search?: string; includeInactive?: boolean } = {}
  ): Promise<ApiResponse<PlatformOrganisation[]>> {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.includeInactive) params.set('include_inactive', 'true');
    const qs = params.toString();
    return apiService.get(`/platform/organisations${qs ? `?${qs}` : ''}`);
  }

  async getUsers(
    opts: { search?: string; filter?: PlatformUserFilter; limit?: number; offset?: number } = {}
  ): Promise<ApiResponse<{ users: PlatformUser[]; total: number; limit: number; offset: number }>> {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.filter) params.set('filter', opts.filter);
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.offset != null) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return apiService.get(`/platform/users${qs ? `?${qs}` : ''}`);
  }

  async getClientErrors(
    opts: { includeResolved?: boolean; limit?: number; offset?: number } = {}
  ): Promise<ApiResponse<{ errors: ClientErrorReport[]; total: number; limit: number; offset: number }>> {
    const params = new URLSearchParams();
    if (opts.includeResolved) params.set('include_resolved', 'true');
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.offset != null) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return apiService.get(`/platform/client-errors${qs ? `?${qs}` : ''}`);
  }

  async getClientErrorSummary(): Promise<ApiResponse<ClientErrorSummary>> {
    return apiService.get('/platform/client-errors/summary');
  }

  /** Resolving keeps the row; a recurrence reopens it automatically and re-alerts. */
  async setClientErrorResolved(id: number, resolved: boolean): Promise<ApiResponse<unknown>> {
    return apiService.patch(`/platform/client-errors/${id}`, { resolved });
  }
}

export default new PlatformService();
